import { createSlice } from "@reduxjs/toolkit";
import { SelectedProduct, PostCode } from "../model/product";
import { get_today_plus_object } from "../Validation";
import { MIN_DAYS_OTHER_AREA } from "../Variable";

interface ReservationState {
    reservation_division               : number;
    reservation_with_removal           : number;
    selected_product                   : SelectedProduct[];
    install_selected_product           : SelectedProduct[];
    reservation_high_status            : number;
    postcode_3                         : PostCode;
    postcode_4                         : PostCode;
    install_postcode                   : PostCode;
    install_reservation_date           : string;
    install_reservation_time           : number;
    reservation_direct_contact_manager : boolean;
    product_consumption_status         : number;
    construction_reservation_expected_date: string;
    reservation_client_name            : string;
    reservation_client_particulars     : string;
    reservation_client_request         : string;
    reservation_date                   : string;
    reservation_time                   : number;
    is_direct_product                  : boolean;
    direct_index_list                  : number[];
    approve_cert                       : boolean;
    reservation_client_phone_number    : string;
    reservation_manager_phone_number   : string;
    reservation_square_meter           : number;
    reservation_space                  : number;
};

const initialState = {
    reservation_division    : 0,
    reservation_with_removal: 0,
    selected_product        : [],
    install_selected_product: [],
    reservation_high_status : 0,
    postcode_3 : {
        zip_code       : "",
        address        : "",
        detail_address : "",
        sido           : "",
        sigungu        : "",
    },
    postcode_4 : {
        zip_code       : "",
        address        : "",
        detail_address : "",
        sido           : "",
        sigungu        : "",
    },
    install_postcode : {
        zip_code       : "",
        address        : "",
        detail_address : "",
        sido           : "",
        sigungu        : "",
    },
    install_reservation_date           : get_today_plus_object(MIN_DAYS_OTHER_AREA).year_month_day,
    install_reservation_time           : 0,
    reservation_direct_contact_manager : false,
    product_consumption_status    : 0,
    construction_reservation_expected_date: '',
    reservation_client_name            : "",
    reservation_client_particulars     : "",
    reservation_client_request         : "",
    reservation_date                   : get_today_plus_object(MIN_DAYS_OTHER_AREA).year_month_day,
    reservation_time                   : 0,
    is_direct_product                  : false,
    direct_index_list                  : [],
    approve_cert                       : false,
    reservation_client_phone_number    : "",
    reservation_manager_phone_number   : "",
    reservation_square_meter           : 0,
    reservation_space                  : 0,
} as ReservationState;

const ReservationSlice = createSlice({
    name : 'reservation_data',
    initialState,
    reducers : {
        reduxChangeReservationDivision : (state, action) => {
            state.reservation_division = action.payload;
            if (action.payload !== 4) {
                state.reservation_with_removal = 0;
            }
        },
        reduxChangeReservationWithRemoval : (state, action) => {
            state.reservation_with_removal = action.payload;
        },
        reduxAddReservationSelectedProduct : (state, action) => {
            state.selected_product.push(action.payload);
        },
        reduxSubtractReservationSelectedProduct : (state, action) => {
            state.selected_product.splice(action.payload, 1);
        },
        reduxDecreaseReservationSelectedProductQuantity : (state, action) => {
            state.selected_product[action.payload].quantity--;
        },
        reduxIncreaseReservationSelectedProductQuantity : (state, action) => {
            state.selected_product[action.payload].quantity++;
        },
        reduxChangeReservationSelectedProduct : (state, action) => {
            state.selected_product = action.payload;
        },
        reduxAddInstallSelectedProduct : (state, action) => {
            state.install_selected_product.push(action.payload);
        },
        reduxSubtractInstallSelectedProduct : (state, action) => {
            state.install_selected_product.splice(action.payload, 1);
        },
        reduxDecreaseInstallSelectedProductQuantity : (state, action) => {
            state.install_selected_product[action.payload].quantity--;
        },
        reduxIncreaseInstallSelectedProductQuantity : (state, action) => {
            state.install_selected_product[action.payload].quantity++;
        },
        reduxChangeInstallSelectedProduct : (state, action) => {
            state.install_selected_product = action.payload;
        },
        reduxChangeReservationHighStatus : (state, action) => {
            state.reservation_high_status = action.payload;
        },
        reduxChangePostcode3 : (state, action) => {
            state.postcode_3 = action.payload;
        },
        reduxChangePostcode4 : (state, action) => {
            state.postcode_4 = action.payload;
        },
        reduxChangeCustomerDetailAddress3 : (state, action) => {
            state.postcode_3.detail_address = action.payload;
        },
        reduxChangeCustomerDetailAddress4 : (state, action) => {
            state.postcode_4.detail_address = action.payload;
        },
        reduxChangeInstallPostcode : (state, action) => {
            state.install_postcode = action.payload;
        },
        reduxChangeInstallDetailAddress : (state, action) => {
            state.install_postcode.detail_address = action.payload;
        },
        reduxChangeInstallReservationDate : (state, action) => {
            state.install_reservation_date = action.payload;
        },
        reduxChangeInstallReservationTime : (state, action) => {
            state.install_reservation_time = action.payload;
        },
        reduxChangeReservationDirectContactManager : (state, action) => {
            state.reservation_direct_contact_manager = action.payload;
        },
        reduxChangeProductConsumptionStatus : (state, action) => {
            state.product_consumption_status = action.payload;
        },
        reduxChangeConstructionReservationExpectedDate : (state, action) => {
            state.construction_reservation_expected_date = action.payload;
        },
        reduxChangeReservationClientName : (state, action) => {
            state.reservation_client_name = action.payload;
        },
        reduxChangeReservationClientParticulars : (state, action) => {
            state.reservation_client_particulars = action.payload;
        },
        reduxChangeReservationClientRequest : (state, action) => {
            state.reservation_client_request = action.payload;
        },
        reduxChangeReservationDate : (state, action) => {
            state.reservation_date = action.payload;
        },
        reduxChangeReservationTime : (state, action) => {
            state.reservation_time =  action.payload;
        },
        reduxChangeReservationIsDirectProduct : (state, action) => {
            state.is_direct_product = action.payload;
        },
        reduxChangeReservationDirectProductIndexList : (state, action) => {
            state.direct_index_list = action.payload;
        },
        reduxChangeReservationApproveCert : (state, action) => {
            state.approve_cert = action.payload;
        },
        reduxChangeReservationClientPhoneNumber : (state, action) => {
            state.reservation_client_phone_number = action.payload;
        },
        reduxChangeReservationManagerPhoneNumber : (state, action) => {
            state.reservation_manager_phone_number = action.payload;
        },
        reduxChangeReservationSquareMeter : (state, action) => {
            state.reservation_square_meter = action.payload;
        },
        reduxChangeReservationSpace : (state, action) => {
            state.reservation_space = action.payload;
        },
        reduxResetReservation : (state) => {
            Object.assign(state, initialState);
        }
    }
});

export const {
    reduxChangeReservationDivision,
    reduxChangeReservationWithRemoval,
    reduxAddReservationSelectedProduct,
    reduxSubtractReservationSelectedProduct,
    reduxDecreaseReservationSelectedProductQuantity,
    reduxIncreaseReservationSelectedProductQuantity,
    reduxChangeReservationSelectedProduct,
    reduxAddInstallSelectedProduct,
    reduxSubtractInstallSelectedProduct,
    reduxDecreaseInstallSelectedProductQuantity,
    reduxIncreaseInstallSelectedProductQuantity,
    reduxChangeInstallSelectedProduct,
    reduxChangeReservationHighStatus,
    reduxChangePostcode3,
    reduxChangePostcode4,
    reduxChangeCustomerDetailAddress3,
    reduxChangeCustomerDetailAddress4,
    reduxChangeInstallPostcode,
    reduxChangeInstallDetailAddress,
    reduxChangeInstallReservationDate,
    reduxChangeInstallReservationTime,
    reduxChangeReservationDirectContactManager,
    reduxChangeProductConsumptionStatus,
    reduxChangeConstructionReservationExpectedDate,
    reduxChangeReservationClientName,
    reduxChangeReservationClientParticulars,
    reduxChangeReservationClientRequest,
    reduxChangeReservationDate,
    reduxChangeReservationTime,
    reduxChangeReservationIsDirectProduct,
    reduxChangeReservationDirectProductIndexList,
    reduxChangeReservationApproveCert,
    reduxChangeReservationClientPhoneNumber,
    reduxChangeReservationManagerPhoneNumber,
    reduxChangeReservationSquareMeter,
    reduxChangeReservationSpace,
    reduxResetReservation
} = ReservationSlice.actions;

export default ReservationSlice.reducer;